import { isDefined, isNotDefined } from '@sgme/fp';
import { TagOption } from 'components/shared/Tags/model';
import { Underlying } from 'models/underlying';
import { UnderlyingCodeType } from 'models/underlying/base';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { definedTagValue } from 'utils/tags';
import { translateUnderlyingToTagOption } from 'utils/underlying/translate-underlying-to-tag-option';

const isValidTag = (tag: TagOption<Underlying>) => tag.rawValue?.hasMissingCodes !== true;

function filterTags(newTags: readonly TagOption<Underlying>[]) {
  return newTags.filter(definedTagValue).filter(isValidTag);
}

function tagEquals(tag1: TagOption<Underlying>, tag2: TagOption<Underlying>) {
  return (
    tag1.label === tag2.label &&
    tag1.isDisabled === tag2.isDisabled &&
    tag1.isHighlighted === tag2.isHighlighted &&
    tag1.value === tag2.value &&
    tag1.rawValue?.id === tag2.rawValue?.id
  );
}

type UnderlyingsSyncMode = 'merge' | 'replace' | 'keep_different';
type Options = {
  codeType: UnderlyingCodeType;
  underlyingsSyncMode: UnderlyingsSyncMode;
  removeInvalidTagsOnUnderlyingsChange: boolean;
} & (
  | { isMulti: true; onChange: (underlyings: readonly Underlying[]) => void; underlyings: readonly Underlying[] }
  | { isMulti: false; onChange: (underlying?: Underlying) => void; underlying?: Underlying }
);
type MultiOrSingleOptions = { isMulti: boolean; underlyings: readonly Underlying[]; underlying?: Underlying };
const EMPTY_ARRAY: readonly Underlying[] = [];
/**
 * Custom React hook for managing underlying tags with validation and synchronization.
 *
 * Provides functionality to manage tags representing financial underlyings, including:
 * - Tag addition, removal, and validation
 * - Synchronization between tags and underlying data
 * - Highlight toggling for tags
 * - Filtering of valid/invalid tags
 *
 * @param {Object} options - Configuration options for the hook
 * @param {UnderlyingCodeType} options.codeType - The type of code to use for underlying identification
 * @param {UnderlyingsSyncMode} options.underlyingsSyncMode - Synchronization strategy when underlyings change:
 *   - 'merge': Combines new underlyings with existing tags, avoiding duplicates
 *   - 'replace': Replaces all tags with new underlyings
 *   - 'keep_different': Keeps only tags that don't match any of the new underlyings
 * @param {boolean} options.removeInvalidTagsOnUnderlyingsChange - If true, automatically removes invalid tags when underlyings are updated
 * @param {boolean} options.isMulti - Whether multiple underlyings can be selected
 * @param {Function} options.onChange - Callback function when underlyings change
 * @param {Underlying[] | Underlying} [options.underlyings | options.underlying] - Current underlying(s) depending on isMulti flag
 *
 * @returns {Object} Object containing tag management functions and state:
 *   - onTagRemoved: Function to remove a tag by label
 *   - onTagsChanged: Function to update tags with optional underlying synchronization
 *   - onTagHighlightToggled: Function to toggle highlight state of a tag
 *   - tags: Current array of all tags
 *   - validTags: Array of valid tags only
 *   - invalidTags: Array of invalid tags only
 *   - newValidTags: Array of valid tags not yet in underlyings
 *   - newTags: Array of all tags not yet in underlyings
 *   - clearAllTags: Function to remove all tags
 *   - updateUnderlyings: Function to synchronize underlyings with current tags
 *   - onUnderlyingRemoved: Function to remove an underlying
 *   - onUnderlyingRemovedById: Function to remove an underlying by ID
 *   - hasTags: Boolean indicating if any tags exist
 *
 * @example
 * ```typescript
 * const { tags, onTagsChanged, validTags } = useUnderlyingsTags({
 *   codeType: 'Bloomberg',
 *   underlyingsSyncMode: 'merge',
 *   removeInvalidTagsOnUnderlyingsChange: true,
 *   isMulti: true,
 *   onChange: (underlyings) => console.log(underlyings),
 *   underlyings: []
 * });
 * ```
 */
export const useUnderlyingsTags = ({
  codeType,
  underlyingsSyncMode,
  removeInvalidTagsOnUnderlyingsChange,
  ...options
}: Options) => {
  const [tags, setTags] = useState<readonly TagOption<Underlying>[]>([]);
  const multiOrSingleOptions = options as MultiOrSingleOptions;
  const underlyings = useMemo(() => {
    if (multiOrSingleOptions.isMulti) {
      return multiOrSingleOptions.underlyings;
    }
    if (multiOrSingleOptions.isMulti === false) {
      if (isDefined(multiOrSingleOptions.underlying)) {
        return [multiOrSingleOptions.underlying];
      } else {
        return EMPTY_ARRAY;
      }
    }
    return EMPTY_ARRAY;
  }, [multiOrSingleOptions.isMulti, multiOrSingleOptions.underlying, multiOrSingleOptions.underlyings]);

  useEffect(() => {
    const tagsFromUnderlyings = underlyings.map<TagOption<Underlying>>(underlying =>
      translateUnderlyingToTagOption(underlying, codeType),
    );
    if (underlyingsSyncMode === 'keep_different') {
      setTags(previousTags => {
        const otherTags = previousTags.filter(tag => underlyings.every(u => u.id !== tag.rawValue?.id));
        return otherTags;
      });
    } else if (underlyingsSyncMode === 'merge') {
      setTags(previousTags => [
        ...tagsFromUnderlyings,
        ...previousTags.filter(tag => !underlyings.some(u => u.id === tag.rawValue?.id)),
      ]);
    } else {
      setTags(() => tagsFromUnderlyings);
    }
  }, [underlyings, codeType, underlyingsSyncMode]);

  const onUnderlyingsArrayChanged = useCallback(
    (underlyings: readonly Underlying[], updateUnderlyings: boolean) => {
      if (!updateUnderlyings) {
        return;
      }
      if (options.isMulti) {
        options.onChange(underlyings);
      } else {
        options.onChange(underlyings[underlyings.length - 1]);
      }
    },
    [options],
  );

  const onTagRemoved = useCallback(
    (labelToRemove: string) => {
      const tagToRemoveIndex = tags.findIndex(tag => tag.label === labelToRemove);
      if (tagToRemoveIndex < 0) {
        return;
      }
      onUnderlyingsArrayChanged(
        underlyings.filter(u => u.id !== tags[tagToRemoveIndex]?.rawValue?.id),
        true,
      );
      const newTags = tags.slice(0, tagToRemoveIndex).concat(tags.slice(tagToRemoveIndex + 1));

      setTags(newTags);
    },
    [onUnderlyingsArrayChanged, tags, underlyings],
  );

  const clearAllTags = useCallback(
    (updateUnderlyings: boolean) => {
      setTags([]);
      onUnderlyingsArrayChanged([], updateUnderlyings);
    },
    [onUnderlyingsArrayChanged],
  );

  const onUnderlyingRemoved = useCallback(
    (underlying: Underlying, updateUnderlyings: boolean) => {
      onUnderlyingsArrayChanged(
        underlyings.filter(u => u.id !== underlying.id),
        updateUnderlyings,
      );
      setTags(previousTags => previousTags.filter(tag => tag.rawValue?.id !== underlying.id));
    },
    [onUnderlyingsArrayChanged, underlyings],
  );

  const onUnderlyingRemovedById = useCallback(
    (id: string, updateUnderlyings: boolean) => {
      const underlyingToRemove = underlyings.find(u => u.id === id);
      if (!underlyingToRemove) {
        return;
      }
      onUnderlyingRemoved(underlyingToRemove, updateUnderlyings);
    },
    [onUnderlyingRemoved, underlyings],
  );
  const _updateUnderlyings = useCallback(
    (currentTags: readonly TagOption<Underlying>[]) => {
      const validTags = filterTags(currentTags);
      if (removeInvalidTagsOnUnderlyingsChange) {
        setTags(validTags);
      }
      const underlyingsFromTags = validTags.map(tag => tag.rawValue);
      const underlyingsToUpdate = underlyings.filter(u => underlyingsFromTags.every(tag => tag.id !== u.id));
      if (underlyings.length === 0 && underlyingsFromTags.length === 0) {
        return;
      }
      if (options.isMulti === false) {
        onUnderlyingsArrayChanged(underlyingsFromTags, true);
      } else {
        onUnderlyingsArrayChanged([...underlyingsToUpdate, ...underlyingsFromTags], true);
      }
    },
    [onUnderlyingsArrayChanged, removeInvalidTagsOnUnderlyingsChange, underlyings, options.isMulti],
  );

  const updateUnderlyings = useCallback(() => _updateUnderlyings(tags), [_updateUnderlyings, tags]);

  const onTagHighlightToggled = useCallback(
    (labelToToggle: string) => {
      setTags(tags.map(tag => (tag.label === labelToToggle ? { ...tag, isHighlighted: !tag.isHighlighted } : tag)));
    },
    [tags],
  );

  return {
    onTagRemoved,
    onTagsChanged: useCallback(
      (newTags: readonly TagOption<Underlying>[], updateUnderlyings: boolean, action: 'add' | 'set' = 'set') => {
        if (action === 'add') {
          newTags = [...tags, ...newTags.filter(newTag => tags.every(tag => tag.label !== newTag.label))];
        }
        if (newTags.length === tags.length && newTags.every(t => tags.some(existingTag => tagEquals(existingTag, t)))) {
          return;
        }
        setTags(newTags);
        if (updateUnderlyings) {
          _updateUnderlyings(newTags);
        }
      },
      [_updateUnderlyings, tags],
    ),
    onTagHighlightToggled,
    tags,
    validTags: useMemo(() => {
      return filterTags(tags);
    }, [tags]),
    invalidTags: useMemo(() => {
      return tags.filter(tag => isNotDefined(tag.rawValue) || tag.rawValue?.hasMissingCodes === true);
    }, [tags]),
    newValidTags: useMemo(
      () => filterTags(tags).filter(tag => underlyings.every(u => u.id !== tag.rawValue.id)),
      [tags, underlyings],
    ),
    newTags: useMemo(() => tags.filter(tag => underlyings.every(u => u.id !== tag?.rawValue?.id)), [tags, underlyings]),
    clearAllTags,
    updateUnderlyings,
    onUnderlyingRemoved,
    hasTags: useMemo(() => tags.length > 0, [tags]),
    onUnderlyingRemovedById,
  };
};
